/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AddMealInsightFeedbackRequest } from '../models/AddMealInsightFeedbackRequest';
import type { CreateMealInsightRequest } from '../models/CreateMealInsightRequest';
import type { RefineMealInsightRequest } from '../models/RefineMealInsightRequest';
import type { SuccessBoolean } from '../models/SuccessBoolean';
import type { SuccessCreateMealInsightResponse } from '../models/SuccessCreateMealInsightResponse';
import type { SuccessMealInsightStatsResponse } from '../models/SuccessMealInsightStatsResponse';
import type { SuccessPageMealInsightHistoryResponse } from '../models/SuccessPageMealInsightHistoryResponse';
import type { SuccessVoid } from '../models/SuccessVoid';
import type { UpdateMealInsightRequest } from '../models/UpdateMealInsightRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class MealInsightControllerService {

    /**
     * Creates a meal insight for a given file. The file must be owned by the authenticated user.
     * Without 'takenAtOverride' the meal's date/time is resolved from the image's EXIF metadata in the following order: (1) DateTimeOriginal + OffsetTimeOriginal; (2) DateTimeOriginal with the offset derived from the GPS clock (GPSDateStamp/GPSTimeStamp are UTC by spec, so their delta from the wall clock is the recorded offset); (3) DateTimeOriginal anchored in the user's timezone when no offset survives; (4) the upload time in the user's timezone when the image carries no usable EXIF datetime at all. A capture time later than the upload is treated as a device-clock error and also falls back to the upload time. 'takenAtOverride' must not fall on a day that has not yet started in the user's timezone.
     * @param requestBody
     * @returns SuccessCreateMealInsightResponse OK
     * @throws ApiError
     */
    public static createMealInsight(
        requestBody: CreateMealInsightRequest,
    ): CancelablePromise<SuccessCreateMealInsightResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v1/mealinsight',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Corrects the AI's reading of a meal photo with the member's own free text.
     * Re-analyses the same photo together with the member's correction and replaces the insight in place - one eaten meal stays one log entry, so 'id' and 'takenAt' are unchanged and the day/week totals follow. Macros are re-derived rather than patched, and where the text disagrees with the photo the text wins. May be called repeatedly: each call corrects the current reading, and the member's earlier corrections still apply. Correcting a REJECTED insight un-rejects it, since the member describing what they ate contradicts the rejection. A failure changes nothing, so the client can simply retry.
     * @param mealInsightId
     * @param requestBody
     * @returns SuccessCreateMealInsightResponse The refined insight, ready to swap into the card.
     * @throws ApiError
     */
    public static refineMealInsight(
        mealInsightId: string,
        requestBody: RefineMealInsightRequest,
    ): CancelablePromise<SuccessCreateMealInsightResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v1/mealinsight/insights/{mealInsightId}/refine',
            path: {
                'mealInsightId': mealInsightId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `The correction was blank or over 500 characters, the meal insights feature is inactive, or the model could not produce a usable reading.`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `No such meal insight for this member, or its photo is gone.`,
                409: `The insight was corrected again while this reading was being produced, so this one is out of date. Refetch the insight and correct it again.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Adds feedback to a meal insight.
     * @param mealInsightId
     * @param requestBody
     * @returns SuccessVoid OK
     * @throws ApiError
     */
    public static addMealInsightFeedback(
        mealInsightId: string,
        requestBody: AddMealInsightFeedbackRequest,
    ): CancelablePromise<SuccessVoid> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v1/mealinsight/insights/{mealInsightId}/feedback',
            path: {
                'mealInsightId': mealInsightId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Deletes a meal insight.
     * @param mealInsightId
     * @returns SuccessVoid OK
     * @throws ApiError
     */
    public static deleteMealInsight(
        mealInsightId: string,
    ): CancelablePromise<SuccessVoid> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/v1/mealinsight/insights/{mealInsightId}',
            path: {
                'mealInsightId': mealInsightId,
            },
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Updates a meal insight. Only the provided fields are changed.
     * 'takenAt' must not fall on a day that has not yet started in the user's timezone.
     * @param mealInsightId
     * @param requestBody
     * @returns SuccessVoid OK
     * @throws ApiError
     */
    public static updateMealInsight(
        mealInsightId: string,
        requestBody: UpdateMealInsightRequest,
    ): CancelablePromise<SuccessVoid> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/v1/mealinsight/insights/{mealInsightId}',
            path: {
                'mealInsightId': mealInsightId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Get aggregated nutrition stats for the authenticated user
     * Returns aggregated nutrition stats for a given period. Supported types: 'day' (single day) and 'week' (ISO week containing the given date). 'date' is interpreted in the user's timezone and defaults to today. For 'type=day' it must not be in the future.
     * @param type
     * @param date
     * @returns SuccessMealInsightStatsResponse Aggregated nutrition stats for the requested period.
     * @throws ApiError
     */
    public static getMealInsightStats(
        type: 'day' | 'week',
        date?: string,
    ): CancelablePromise<SuccessMealInsightStatsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/mealinsight/stats',
            query: {
                'type': type,
                'date': date,
            },
            errors: {
                400: `Unsupported 'type', or a future 'date' for 'type=day'.`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Get the authenticated user's meal insight history
     * Returns the authenticated user's meal insights, most recent first (by meal time), with pagination. Filtering and sorting are driven by the meal time (takenAt), which defaults to the upload time when no EXIF datetime or override is available. Optionally filter by meal identification status, a closed date range (from/to, both inclusive, interpreted in the user's timezone), or a single day. The 'day' parameter cannot be combined with 'from'/'to'.
     * @param mealIdentificationFilter
     * @param from
     * @param to
     * @param day
     * @param page Zero-based page index (0..N)
     * @param size The size of the page to be returned
     * @param sort Sorting criteria in the format: property(,asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
     * @returns SuccessPageMealInsightHistoryResponse OK
     * @throws ApiError
     */
    public static getMealInsightHistory(
        mealIdentificationFilter?: 'IDENTIFIED_ONLY' | 'UNIDENTIFIED_ONLY' | 'BOTH',
        from?: string,
        to?: string,
        day?: string,
        page?: any,
        size?: any,
        sort?: any,
    ): CancelablePromise<SuccessPageMealInsightHistoryResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/mealinsight/insights',
            query: {
                'mealIdentificationFilter': mealIdentificationFilter,
                'from': from,
                'to': to,
                'day': day,
                'page': page,
                'size': size,
                'sort': sort,
            },
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Returns whether the authenticated user has ever used meal insights (including deleted ones).
     * @returns SuccessBoolean OK
     * @throws ApiError
     */
    public static hasAnyMealInsights(): CancelablePromise<SuccessBoolean> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/mealinsight/exists',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Returns whether the authenticated user had a Daily Insight unlocked on any day before today (in the user's timezone).
     * @returns SuccessBoolean OK
     * @throws ApiError
     */
    public static hasPreviouslyUnlockedDailyInsight(): CancelablePromise<SuccessBoolean> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/mealinsight/daily-insight/previously-unlocked',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

}
